import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { showToast } from '@/utils/toast';

/** 与后端 TemuGoodsService.SYNC_PAGE_SIZE / 平台 pageSize 上限对齐 */
const DEFAULT_MAX_PAGES = 200;
const ABS_MAX_PAGES = 500;
const TEMU_PAGE_SIZE = 100;
const MAX_SKC = 100;

function parseProductSkcIds(raw) {
  if (!raw || !String(raw).trim()) return [];
  const seen = new Set();
  const out = [];
  for (const part of String(raw).split(/[,，\s\n\r\t;；]+/)) {
    const token = part.trim();
    if (!token) continue;
    if (!/^\d+$/.test(token)) continue;
    const id = Number(token);
    if (!Number.isSafeInteger(id) || id <= 0 || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= MAX_SKC) break;
  }
  return out;
}

/**
 * TEMU 产品「同步更新」弹窗。
 * 对齐平台 bg.goods.list.get / bg.glo.goods.list.get：
 * - 默认全量：page + pageSize（≤100）翻页
 * - 指定同步：productSkcIds（货品 SKC ID 列表），不是 SHEIN 的 SPU
 */
export default function TemuProductSyncModal({
  open,
  confirming = false,
  defaultMaxPages = DEFAULT_MAX_PAGES,
  onConfirm,
  onClose,
}) {
  const [mode, setMode] = useState('default'); // default | custom
  const [maxPages, setMaxPages] = useState(String(defaultMaxPages));
  const [skcText, setSkcText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setMode('default');
    setMaxPages(String(defaultMaxPages || DEFAULT_MAX_PAGES));
    setSkcText('');
  }, [open, defaultMaxPages]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !confirming && !submitting) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, confirming, submitting, onClose]);

  const productSkcIds = useMemo(() => parseProductSkcIds(skcText), [skcText]);
  const invalidTokens = useMemo(() => {
    if (!skcText || !String(skcText).trim()) return [];
    const bad = [];
    for (const part of String(skcText).split(/[,，\s\n\r\t;；]+/)) {
      const token = part.trim();
      if (!token) continue;
      if (!/^\d+$/.test(token)) bad.push(token);
    }
    return bad.slice(0, 5);
  }, [skcText]);

  if (!open) return null;

  const busy = confirming || submitting;

  const handleConfirm = async () => {
    if (confirming || submittingRef.current) return;
    if (mode === 'custom') {
      if (invalidTokens.length > 0) {
        showToast(`SKC ID 须为纯数字，已忽略无效项：${invalidTokens.join('、')}`, 'warning');
      }
      if (productSkcIds.length === 0) {
        showToast(fixedUi("指定同步请填写至少一个货品 SKC ID（productSkcId）"), 'error');
        return;
      }
      if (productSkcIds.length > MAX_SKC) {
        showToast(`最多指定 ${MAX_SKC} 个 SKC`, 'error');
        return;
      }
      submittingRef.current = true;
      setSubmitting(true);
      try {
        await onConfirm?.({ productSkcIds });
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
      return;
    }

    const pages = Number(maxPages);
    if (!Number.isFinite(pages) || pages < 1) {
      showToast(fixedUi("请填写有效的同步分页数（≥1）"), 'error');
      return;
    }
    if (pages > ABS_MAX_PAGES) {
      showToast(`${fixedUi('同步分页数最多')} ${ABS_MAX_PAGES}`, 'error');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onConfirm?.({
        maxPages: Math.min(ABS_MAX_PAGES, Math.max(1, Math.floor(pages))),
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="tpo-batch-modal" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={fixedUi("关闭")}
        disabled={busy}
        onClick={() => !busy && onClose?.()}
      />
      <div
        className="tpo-batch-modal__panel tpo-sync-range-modal__panel tpo-product-sync-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-product-sync-modal-title"
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-product-sync-modal-title">{fixedUi("产品同步更新")}</h2>
          <button
            type="button"
            className="tpo-btn tpo-btn--ghost tpo-btn--sm"
            disabled={busy}
            onClick={onClose}
          >
            {fixedUi("关闭")}
          </button>
        </div>
        <div className="tpo-batch-modal__body">

          <div className="tpo-sync-range-modal__presets" role="group" aria-label={fixedUi("同步方式")}>
            <button
              type="button"
              className={`tpo-btn tpo-btn--sm ${mode === 'default' ? 'tpo-btn--primary' : 'tpo-btn--secondary'}`}
              disabled={busy}
              onClick={() => setMode('default')}
            >
              {fixedUi("默认全量")}
            </button>
            <button
              type="button"
              className={`tpo-btn tpo-btn--sm ${mode === 'custom' ? 'tpo-btn--primary' : 'tpo-btn--secondary'}`}
              disabled={busy}
              onClick={() => setMode('custom')}
            >
              {fixedUi("指定 SKC")}
            </button>
          </div>
          <div className="tpo-sync-range-modal__fields">
            {mode === 'default' ? (
              <label className="tpo-field tpo-field--block">
                <span className="tpo-field__label">
                  {fixedUi('同步分页数')} (pageSize={TEMU_PAGE_SIZE})
                </span>
                <input
                  className="tpo-input tpo-product-sync-modal__input"
                  type="number"
                  min={1}
                  max={ABS_MAX_PAGES}
                  step={1}
                  value={maxPages}
                  disabled={busy}
                  onChange={(e) => setMaxPages(e.target.value)}
                />
              </label>
            ) : (
              <label className="tpo-field tpo-field--block">
                <span className="tpo-product-sync-modal__label-row">
                  <span className="tpo-field__label">
                    {fixedUi('货品 SKC ID')} ({fixedUi('最多')} {MAX_SKC})
                  </span>
                  <span className="tpo-product-sync-modal__count">{productSkcIds.length}/{MAX_SKC}</span>
                </span>
                <textarea
                  className="tpo-input tpo-product-sync-modal__input tpo-product-sync-modal__spu"
                  rows={6}
                  placeholder={fixedUi("多个 productSkcId 用换行、逗号或空格分隔，例如：5407624918")}
                  value={skcText}
                  required
                  aria-required="true"
                  disabled={busy}
                  onChange={(e) => setSkcText(e.target.value)}
                />
              </label>
            )}
          </div>
        </div>
        <div className="tpo-batch-modal__foot">
          <button
            type="button"
            className="tpo-btn tpo-btn--ghost"
            disabled={busy}
            onClick={onClose}
          >
            {fixedUi("取消")}
          </button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary"
            disabled={busy || (mode === 'custom' && productSkcIds.length === 0)}
            onClick={handleConfirm}
          >
            {busy ? fixedUi("启动中…") : fixedUi("开始同步")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
